import { DateTime } from 'luxon'
import { memoize } from 'lodash-es'

let TODAY = DateTime.local().setZone('America/Chicago').startOf('day')

/**
 * The DateLogic module contains logic methods for date calculations.
 *
 * NOTE: For Shedd, the week starts on Sunday and runs through the following
 * Saturday. Luxon treats Monday as the first day of the week. This means we
 * have to be a little bit extra careful to handle the difference when working
 * with Luxon week methods.
 */
const DateLogic = {
  today: TODAY,

  getDaysInWeek: memoize(
    date => {
      // necessary due to Shedd / Luxon week start difference
      let start =
        date.weekday === 7 ? date : date.startOf('week').minus({ days: 1 })
      return DateLogic.getDaysInRange(start, start.plus({ days: 6 }))
    },
    date => date.ts
  ),

  getDaysInRange: memoize(
    (start, end) => {
      let days = new Array()
      let count = Math.round(end.diff(start).as('days') + 1)

      for (let i = 0; i < count; i++) {
        days.push(start.plus({ days: i }))
      }

      return days
    },
    (start, end) => `${start.ts}-${end.ts}`
  ),

  getWeekStartEnd: memoize(
    date => {
      let days = DateLogic.getDaysInWeek(date)
      return [days[0], days[6]]
    },
    date => date.ts
  ),

  getCurrentWeekStartEnd: () => {
    return DateLogic.getWeekStartEnd(TODAY)
  },

  isSingleDay([start, end]) {
    return (
      start.year === end.year &&
      start.month === end.month &&
      start.day === end.day
    )
  },

  isCurrentDay(range) {
    let [start] = range

    return (
      DateLogic.isSingleDay(range) &&
      start.year === TODAY.year &&
      start.month === TODAY.month &&
      start.day === TODAY.day
    )
  },

  isCurrentWeek(range) {
    let [start, end] = range
    let [weekStart, weekEnd] = DateLogic.getCurrentWeekStartEnd()

    return start.equals(weekStart) && end.equals(weekEnd)
  }
}

export default DateLogic
